import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Document, Page } from "react-pdf";

import { API_TARGET, apiClient, apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import { WARDS_BY_LGA } from "@mhj/shared/wards";

// Every ward loaded for any LGA, not just Lapai — a search convenience, not
// a restriction, so this grows automatically as ward data for other LGAs is
// added (R5). Today that's still just Lapai's ten.
const KNOWN_WARDS = Object.values(WARDS_BY_LGA).flat().sort();

type Cycle = { id: string; name: string; code: string; status: string };

type SmsUsage = {
  used: number;
  cap: number | null;
  remaining: number | null;
  cappedThisMonth: number;
  monthStart: string;
};

type ListRow = {
  id: string;
  applicationNo: string | null;
  status: string;
  source: "self" | "officer";
  ward: string;
  gender: string;
  trackName: string | null;
  userName: string;
  userPhone: string | null;
  priorBeneficiaryFlag: boolean;
  documentCount: number;
  submittedAt: string | null;
};

type Award = {
  id: string;
  type: "training" | "cash" | "equipment";
  amount: string | null;
  itemDescription: string | null;
  centre: string | null;
  scheduledDate: string | null;
  status: "pending" | "delivered";
  recordedBy: string;
  supervisorConfirmedBy: string | null;
  physicalVerificationBy: string | null;
};

type Detail = ListRow & {
  trackId: string;
  assignedTrackId: string | null;
  assignedTrackName: string | null;
  trackAwardTypes: string[];
  userEmail: string | null;
  dateOfBirth: string;
  nin: string | null;
  state: string;
  lga: string;
  address: string | null;
  educationLevel: string;
  employmentStatus: string;
  maritalStatus: string;
  dependants: number;
  incomeBand: string;
  hasDisability: boolean;
  disabilityDetails: string | null;
  trackDetail: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  internalNotes: string | null;
  rejectionReason: string | null;
  cycleName: string | null;
  documents: { id: string; docType: string; verified: boolean; uploadedAt: string | null; url: string; isPdf: boolean }[];
  awards: Award[];
  history: {
    empowerment: { applicationNo: string | null; status: string; cycleName: string | null; trackName: string | null }[];
    scholarship: { category: string; status: string; submittedAt: string | null }[];
  };
};

const STATUS_STYLES: Record<string, string> = {
  submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  delivered: "bg-emerald-600 text-white",
  rejected: "bg-red-100 text-red-800",
};

/** D6: the proposed starting list. Editable copy lives in the plan appendix. */
const REJECTION_REASONS = [
  "Application incomplete — required information was missing",
  "Documents provided were unclear or could not be read",
  "Did not meet the age requirement for the selected trade",
  "Outside the local government areas covered by this round",
  "Proof of Niger State origin could not be confirmed",
  "Has previously received support from the programme",
  "More applicants than available places in this trade",
  "Information provided could not be verified",
];

const label = (v: string) => v.replace(/_/g, " ");

export default function AdminEmpowerment() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [filters, setFilters] = useState({ cycleId: "", status: "", ward: "", search: "", flagged: "" });
  const [reason, setReason] = useState(REJECTION_REASONS[0]!);
  const [notes, setNotes] = useState("");
  const [award, setAward] = useState({ type: "training", amount: "", itemDescription: "", centre: "", scheduledDate: "" });
  const [activeDocument, setActiveDocument] = useState<Detail["documents"][number] | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (activeDocument?.isPdf) {
      setPdfPageNumber(1);
      setPdfNumPages(0);
    }
  }, [activeDocument]);

  // The viewer needs the file as a same-origin blob: URL — react-pdf's `file`
  // prop has no typed way to send the auth cookie for a cross-origin fetch,
  // unlike the plain <img> case, which the browser handles for us.
  useEffect(() => {
    if (!activeDocument?.isPdf) {
      setPdfLoading(false);
      setPdfBlobUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      return;
    }

    let cancelled = false;
    setPdfLoading(true);

    void apiClient
      .get<Blob>(activeDocument.url, { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        const url = URL.createObjectURL(response.data);
        setPdfBlobUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return url;
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.error((error as { message?: string })?.message ?? "Unable to load PDF file.");
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeDocument]);

  const { data: cycleData } = useQuery({
    queryKey: ["empowerment", "cycles"],
    queryFn: () => apiRequest<{ cycles: Cycle[] }>("/admin/empowerment/cycles"),
  });

  const { data, error, isLoading } = useQuery({
    queryKey: ["empowerment", "applications", filters],
    queryFn: () =>
      apiRequest<{ applications: ListRow[] }>("/admin/empowerment", {
        params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")),
      }),
  });

  const { data: detailData } = useQuery({
    queryKey: ["empowerment", "application", selected],
    enabled: !!selected,
    queryFn: () => apiRequest<{ application: Detail }>(`/admin/empowerment/${selected}`),
  });

  const { data: summary } = useQuery({
    queryKey: ["empowerment", "summary", filters.cycleId],
    enabled: !!filters.cycleId,
    queryFn: () => apiRequest<any>(`/admin/empowerment/summary/${filters.cycleId}`),
  });

  const { data: smsUsage } = useQuery({
    queryKey: ["empowerment", "sms-usage"],
    queryFn: () => apiRequest<SmsUsage>("/admin/empowerment/sms-usage"),
  });

  useEffect(() => {
    if (error) toast.error((error as Error).message);
  }, [error]);

  useEffect(() => {
    setNotes(detailData?.application.internalNotes ?? "");
  }, [detailData]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["empowerment"] });
  const act = <T,>(fn: () => Promise<T>, success: string) =>
    fn()
      .then(() => { toast.success(success); refresh(); })
      .catch((e: Error) => toast.error(e.message));

  const setStatus = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiRequest(`/admin/empowerment/${selected}/status`, { method: "POST", withCsrf: true, data: body }),
  });

  const detail = detailData?.application;
  const rows = data?.applications ?? [];

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-500">Empowerment</p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">Applications</h2>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <select
            className="rounded-2xl border border-sky-200 px-3 py-2 text-sm"
            value={filters.cycleId}
            onChange={(e) => setFilters({ ...filters, cycleId: e.target.value })}
          >
            <option value="">All cycles</option>
            {(cycleData?.cycles ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="rounded-2xl border border-sky-200 px-3 py-2 text-sm"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Any status</option>
            {["submitted", "under_review", "approved", "rejected", "delivered"].map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
          <select
            className="rounded-2xl border border-sky-200 px-3 py-2 text-sm"
            value={filters.ward}
            onChange={(e) => setFilters({ ...filters, ward: e.target.value })}
          >
            <option value="">Any ward</option>
            {KNOWN_WARDS.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select
            className="rounded-2xl border border-sky-200 px-3 py-2 text-sm"
            value={filters.flagged}
            onChange={(e) => setFilters({ ...filters, flagged: e.target.value })}
          >
            <option value="">Everyone</option>
            <option value="true">Flagged as prior beneficiary</option>
          </select>
          <input
            className="rounded-2xl border border-sky-200 px-3 py-2 text-sm"
            placeholder="Name, phone or ID"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        {summary && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              {summary.total} submitted
            </span>
            {Object.entries(summary.byStatus as Record<string, number>).map(([k, v]) => (
              <span key={k} className={`rounded-full px-3 py-1 ${STATUS_STYLES[k] ?? "bg-slate-100"}`}>
                {label(k)}: {v}
              </span>
            ))}
            {filters.cycleId && (
              <a
                href={`${API_TARGET}/api/admin/empowerment/export/${filters.cycleId}`}
                className="rounded-full border border-blue-200 px-3 py-1 text-blue-700 hover:bg-blue-50"
              >
                Export CSV
              </a>
            )}
          </div>
        )}

        {smsUsage && smsUsage.cap !== null && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`rounded-full px-3 py-1 ${
                smsUsage.remaining !== null && smsUsage.remaining <= smsUsage.cap * 0.1
                  ? "bg-red-100 text-red-700"
                  : smsUsage.remaining !== null && smsUsage.remaining <= smsUsage.cap * 0.25
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600"
              }`}
            >
              SMS this month: {smsUsage.used.toLocaleString()} / {smsUsage.cap.toLocaleString()}
            </span>
            {smsUsage.cappedThisMonth > 0 && (
              <span className="rounded-full bg-red-100 px-3 py-1 text-red-700">
                {smsUsage.cappedThisMonth} message{smsUsage.cappedThisMonth === 1 ? "" : "s"} held
                back by the cap this month
              </span>
            )}
          </div>
        )}
      </div>

      <div className="glass-card rounded-3xl p-6">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No applications match these filters.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelected(selected === row.id ? null : row.id)}
                className={`flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 text-left transition ${
                  selected === row.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-blue-300"
                }`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">
                    {row.userName}
                    {row.priorBeneficiaryFlag && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        Prior beneficiary
                      </span>
                    )}
                    {row.source === "officer" && (
                      <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                        Officer capture
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {row.applicationNo} · {row.trackName} · {row.ward} · {row.documentCount} documents
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[row.status] ?? "bg-slate-100"}`}>
                  {label(row.status)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <div className="glass-card space-y-5 rounded-3xl p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">{detail.userName}</h3>
              <p className="text-sm text-slate-500">
                {detail.applicationNo} · {detail.userPhone} · {detail.cycleName}
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[detail.status] ?? "bg-slate-100"}`}>
              {label(detail.status)}
            </span>
          </div>

          {detail.priorBeneficiaryFlag && (
            <div className="rounded-2xl bg-amber-50 p-4 text-sm">
              <p className="font-semibold text-amber-900">Has received support before</p>
              <ul className="mt-1 space-y-0.5 text-amber-800">
                {detail.history.empowerment.map((h, i) => (
                  <li key={i}>{h.cycleName} — {h.trackName} — {label(h.status)}</li>
                ))}
                {detail.history.scholarship.map((h, i) => (
                  <li key={`s${i}`}>Scholarship ({h.category}) — {label(h.status)}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-amber-700">
                Flagged, not blocked. Your judgement.
              </p>
            </div>
          )}

          <div className="grid gap-4 text-sm sm:grid-cols-2">
            <Detail label="Applied for" value={detail.trackName} />
            <Detail label="Assigned to" value={detail.assignedTrackName ?? detail.trackName} />
            <Detail label="Ward" value={`${detail.ward}, ${detail.lga}`} />
            <Detail label="Date of birth" value={detail.dateOfBirth} />
            <Detail label="Gender" value={detail.gender} />
            <Detail label="NIN" value={detail.nin ?? "Not provided"} />
            <Detail label="Education" value={label(detail.educationLevel)} />
            <Detail label="Occupation" value={label(detail.employmentStatus)} />
            <Detail label="Dependants" value={String(detail.dependants)} />
            <Detail label="Income" value={label(detail.incomeBand)} />
            {detail.hasDisability && (
              <Detail label="Disability" value={detail.disabilityDetails ?? "Declared"} />
            )}
            {detail.bankName && (
              <>
                <Detail label="Bank" value={detail.bankName} />
                <Detail label="Account" value={`${detail.bankAccountNumber} · ${detail.bankAccountName}`} />
              </>
            )}
          </div>

          {detail.trackDetail && (
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">In their words</p>
              <p className="mt-1 text-sm text-slate-800">{detail.trackDetail}</p>
            </div>
          )}

          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Documents</p>
            <div className="mt-2 space-y-2">
              {detail.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-2.5">
                  <div>
                    <span className="text-sm capitalize text-slate-800">{label(doc.docType)}</span>
                    <button
                      type="button"
                      onClick={() => setActiveDocument(doc)}
                      className="ml-3 text-xs font-medium text-blue-600 hover:text-blue-800"
                    >
                      View document
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      act(
                        () =>
                          apiRequest(`/admin/empowerment/${detail.id}/verify-doc`, {
                            method: "POST",
                            withCsrf: true,
                            data: { documentId: doc.id, verified: !doc.verified },
                          }),
                        doc.verified ? "Marked unverified" : "Marked verified",
                      )
                    }
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      doc.verified
                        ? "bg-emerald-600 text-white"
                        : "border border-slate-300 text-slate-600 hover:bg-white"
                    }`}
                  >
                    {doc.verified ? "Verified" : "Mark verified"}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Internal notes</p>
            <textarea
              className="mt-2 min-h-20 w-full rounded-2xl border border-sky-200 px-4 py-2.5 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Never shown to the applicant."
            />
            <button
              type="button"
              onClick={() =>
                act(
                  () =>
                    apiRequest(`/admin/empowerment/${detail.id}/notes`, {
                      method: "POST",
                      withCsrf: true,
                      data: { internalNotes: notes || null },
                    }),
                  "Notes saved",
                )
              }
              className="mt-2 rounded-full border border-slate-300 px-4 py-1.5 text-xs uppercase tracking-wide text-slate-600 hover:bg-slate-50"
            >
              Save notes
            </button>
          </div>

          {detail.status !== "delivered" && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
              {detail.status === "submitted" && (
                <button
                  type="button"
                  onClick={() => act(() => setStatus.mutateAsync({ status: "under_review" }), "Now under review")}
                  className="rounded-full border border-blue-200 px-4 py-2 text-xs font-medium uppercase tracking-wide text-blue-700 hover:bg-blue-50"
                >
                  Start review
                </button>
              )}
              {detail.status !== "approved" && detail.status !== "rejected" && (
                <button
                  type="button"
                  onClick={() => act(() => setStatus.mutateAsync({ status: "approved" }), "Approved")}
                  className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-emerald-500"
                >
                  Approve
                </button>
              )}
              {detail.status !== "rejected" && (
                <>
                  <select
                    className="rounded-2xl border border-slate-300 px-3 py-2 text-xs"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    {REJECTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() =>
                      act(() => setStatus.mutateAsync({ status: "rejected", rejectionReason: reason }), "Recorded as unsuccessful")
                    }
                    className="rounded-full border border-red-300 px-4 py-2 text-xs font-medium uppercase tracking-wide text-red-700 hover:bg-red-50"
                  >
                    Not successful
                  </button>
                </>
              )}
            </div>
          )}

          {(detail.status === "approved" || detail.status === "delivered") && (
            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Awards</p>

              <div className="mt-2 space-y-2">
                {detail.awards.map((a) => {
                  const needsChecks = a.type === "cash" || a.type === "equipment";
                  const iRecorded = a.recordedBy === user?.id;
                  return (
                    <div key={a.id} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">
                          {a.type === "cash"
                            ? `₦${Number(a.amount ?? 0).toLocaleString()}`
                            : a.type === "equipment"
                              ? a.itemDescription
                              : "Training place"}
                          {a.centre && <span className="font-normal text-slate-500"> · {a.centre}</span>}
                        </p>
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                          a.status === "delivered" ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-800"
                        }`}>
                          {a.status}
                        </span>
                      </div>

                      {needsChecks && a.status !== "delivered" && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                          <span className={a.supervisorConfirmedBy ? "text-emerald-700" : "text-slate-500"}>
                            {a.supervisorConfirmedBy ? "✓ Supervisor confirmed" : "Awaiting supervisor"}
                          </span>
                          {!a.supervisorConfirmedBy && (
                            <button
                              type="button"
                              disabled={iRecorded}
                              title={iRecorded ? "Someone else has to confirm an award you recorded" : undefined}
                              onClick={() =>
                                act(
                                  () =>
                                    apiRequest(`/admin/empowerment/awards/${a.id}/confirm`, {
                                      method: "POST", withCsrf: true, data: {},
                                    }),
                                  "Confirmed",
                                )
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Confirm
                            </button>
                          )}
                          <span className={a.physicalVerificationBy ? "text-emerald-700" : "text-slate-500"}>
                            {a.physicalVerificationBy ? "✓ Verified in person" : "Awaiting verification"}
                          </span>
                          {!a.physicalVerificationBy && (
                            <button
                              type="button"
                              onClick={() =>
                                act(
                                  () =>
                                    apiRequest(`/admin/empowerment/awards/${a.id}/verify`, {
                                      method: "POST", withCsrf: true, data: {},
                                    }),
                                  "Verified",
                                )
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 text-slate-700"
                            >
                              Verify in person
                            </button>
                          )}
                        </div>
                      )}

                      {a.status !== "delivered" && (
                        <button
                          type="button"
                          onClick={() =>
                            act(
                              () =>
                                apiRequest(`/admin/empowerment/awards/${a.id}/deliver`, {
                                  method: "POST", withCsrf: true, data: {},
                                }),
                              "Marked as handed over",
                            )
                          }
                          className="mt-3 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-white hover:bg-blue-500"
                        >
                          Mark handed over
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <form
                className="mt-3 grid gap-2 rounded-2xl bg-slate-50 p-4 sm:grid-cols-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  act(
                    () =>
                      apiRequest(`/admin/empowerment/${detail.id}/awards`, {
                        method: "POST",
                        withCsrf: true,
                        data: {
                          type: award.type,
                          amount: award.type === "cash" ? Number(award.amount) : null,
                          itemDescription: award.itemDescription || null,
                          centre: award.centre || null,
                          scheduledDate: award.scheduledDate || null,
                        },
                      }),
                    "Award recorded",
                  );
                }}
              >
                <select
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={award.type}
                  onChange={(e) => setAward({ ...award, type: e.target.value })}
                >
                  {(detail.trackAwardTypes.length ? detail.trackAwardTypes : ["training"]).map((t) => (
                    <option key={t} value={t}>{label(t)}</option>
                  ))}
                </select>
                {award.type === "cash" ? (
                  <input
                    type="number" min={1} required placeholder="Amount in ₦"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={award.amount}
                    onChange={(e) => setAward({ ...award, amount: e.target.value })}
                  />
                ) : award.type === "equipment" ? (
                  <input
                    required placeholder="What is being given"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={award.itemDescription}
                    onChange={(e) => setAward({ ...award, itemDescription: e.target.value })}
                  />
                ) : (
                  <input
                    placeholder="Training centre"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    value={award.centre}
                    onChange={(e) => setAward({ ...award, centre: e.target.value })}
                  />
                )}
                <input
                  type="date"
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={award.scheduledDate}
                  onChange={(e) => setAward({ ...award, scheduledDate: e.target.value })}
                />
                <button
                  type="submit"
                  className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Record award
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {activeDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-4xl rounded-3xl border border-black/10 bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-black/60">
                {label(activeDocument.docType)}
              </p>
              <button
                type="button"
                onClick={() => setActiveDocument(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-black/70 transition hover:border-black/30 hover:text-black"
                aria-label="Close document viewer"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="h-[60vh] overflow-hidden rounded-2xl border border-black/10 bg-slate-50">
              {activeDocument.isPdf ? (
                <div className="flex h-full w-full items-center justify-center overflow-auto">
                  {pdfLoading ? (
                    <p className="text-sm text-black/60">Loading PDF...</p>
                  ) : pdfBlobUrl ? (
                    <Document
                      file={pdfBlobUrl}
                      loading={<p className="text-sm text-black/60">Loading PDF...</p>}
                      onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                      onLoadError={() => toast.error("Unable to preview this PDF.")}
                    >
                      <Page pageNumber={pdfPageNumber} renderTextLayer={false} renderAnnotationLayer={false} />
                    </Document>
                  ) : (
                    <p className="text-sm text-black/60">Failed to load PDF file.</p>
                  )}
                </div>
              ) : (
                <img
                  src={activeDocument.url}
                  alt="Applicant document preview"
                  className="h-full w-full object-contain"
                />
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
              {activeDocument.isPdf && (
                <div className="mr-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPdfPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pdfPageNumber <= 1}
                    className="rounded-full border border-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span className="text-xs text-black/60">
                    Page {pdfPageNumber}{pdfNumPages ? ` / ${pdfNumPages}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPdfPageNumber((p) => (pdfNumPages ? Math.min(pdfNumPages, p + 1) : p + 1))}
                    disabled={!!pdfNumPages && pdfPageNumber >= pdfNumPages}
                    className="rounded-full border border-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setActiveDocument(null)}
                className="rounded-full border border-black/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const doc = activeDocument;
                  setActiveDocument(null);
                  void act(
                    () =>
                      apiRequest(`/admin/empowerment/${detail?.id}/verify-doc`, {
                        method: "POST",
                        withCsrf: true,
                        data: { documentId: doc.id, verified: !doc.verified },
                      }),
                    doc.verified ? "Marked unverified" : "Marked verified",
                  );
                }}
                className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-400"
              >
                {activeDocument.verified ? "Unverify" : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label: name, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{name}</dt>
      <dd className="mt-0.5 capitalize text-slate-900">{value ?? "—"}</dd>
    </div>
  );
}
